import { ChangeDetectorRef, Component, inject, Input, TemplateRef } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DialogRef } from '@ngneat/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { isNil } from 'lodash';
import { MessageService } from 'primeng/api';

import { AuditEntry } from '../common/audit-entry/interfaces';

@UntilDestroy()
@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css'],
  providers: [MessageService],
})
export class SettingsComponent {
  protected readonly dialogRef = inject(DialogRef, { optional: true });

  protected readonly matDialogRef = inject(MatDialogRef, { optional: true });
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Inputs
  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Input()
  cardTitle: string;

  @Input() auditEntry: AuditEntry = undefined;

  @Input() auditEntryLabel?: string;

  /**
   * @deprecated use `valid` instead
   */
  @Input() isValid?: () => boolean;

  @Input() doUpload: () => Promise<void>;

  // eslint-disable-next-line class-methods-use-this
  @Input() refreshData: () => Promise<void> = () => null;

  @Input() resetForm: () => void;

  /**
   * @deprecated use `ng-content` instead
   */
  @Input() editorTemplate?: TemplateRef<unknown>;

  @Input() valid = true;

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  uploadInProgress = false;

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private messageService: MessageService,
    private translocoService: TranslocoService,
  ) {}

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Buttons
  /////////////////////////////////////////////////////////////////////////////////////////////

  public canSubmit(): boolean {
    if (isNil(this.isValid)) {
      return this.valid && !this.uploadInProgress;
    }

    return this.isValid() && !this.uploadInProgress;
  }

  async upload() {
    try {
      this.uploadInProgress = true;
      await this.doUpload();
      await this.refreshData();
      this.successMessage('uploadSuccessTitle', 'success');
    } catch (error) {
      console.error(error);
      this.failureMessage(error);
    } finally {
      this.uploadInProgress = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  cancel() {
    this.resetForm();
    this.changeDetectorRef.detectChanges();
    this.successMessage('clearedSuccessTitle', 'info');
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Success & Failure Messages
  /////////////////////////////////////////////////////////////////////////////////////////////

  successMessage(title: 'uploadSuccessTitle' | 'clearedSuccessTitle', severity: 'success' | 'info') {
    const uploadSuccessTitle = this.translocoService.translate(`settings.${title}`);
    this.messageService.add({ severity, summary: uploadSuccessTitle, life: 3000 });
  }

  failureMessage(errorJson: any) {
    const uploadErrorTitle = this.translocoService.translate('settings.uploadErrorTitle');
    this.messageService.add({ severity: 'error', summary: uploadErrorTitle, detail: JSON.stringify(errorJson) });
  }
}
